import axios from 'axios';
import i18n from '../i18n';

// Global logout handler - will be set by AuthContext
let globalLogoutHandler = null;

// Function to set the logout handler from AuthContext
export const setLogoutHandler = (handler) => {
  globalLogoutHandler = handler;
};

// Auto-detect if we're accessing from network (not localhost)
const isNetworkAccess = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
const serverHost = isNetworkAccess ? window.location.hostname : 'localhost';

// Auto-detect protocol and port based on current page
const isHttps = window.location.protocol === 'https:';
const serverPort = isHttps ? '5443' : '5000';
const serverProtocol = isHttps ? 'https' : 'http';

// For mobile compatibility, prefer HTTP API even when client is HTTPS
const forceHttpApi = process.env.REACT_APP_FORCE_HTTP_API === 'true';
const apiProtocol = forceHttpApi ? 'http' : serverProtocol;
const apiPort = forceHttpApi ? '5000' : serverPort;

export const API_BASE_URL = process.env.REACT_APP_API_URL || `${apiProtocol}://${serverHost}:${apiPort}/api`;

// Debug logging for API URL
/* console.log('🔧 API Configuration:', {
  isHttps,
  serverHost,
  serverPort,
  serverProtocol,
  forceHttpApi,
  apiProtocol,
  apiPort,
  API_BASE_URL
});*/

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const locale = i18n.resolvedLanguage || localStorage.getItem('i18nextLng') || 'fr';
    config.headers['Accept-Language'] = locale;
    config.headers['X-App-Locale'] = locale;
    
    // If data is FormData, remove Content-Type header (browser will set it with boundary)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token expiration and log errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 503 && error.response?.data?.code === 'MAINTENANCE_MODE') {
      const reqUrl = String(error.config?.url || '');
      // Let login / public registration handle 503 + MAINTENANCE_MODE (need full axios error for codes/messages)
      if (reqUrl.includes('/auth/login') || reqUrl.includes('/register-operator')) {
        return Promise.reject(error);
      }
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (globalLogoutHandler) {
        globalLogoutHandler();
      }
      const path = window.location.pathname || '';
      if (!path.startsWith('/login') && !path.startsWith('/operator/register')) {
        window.location.href = '/login';
      }
      const msgKey = 'login.maintenanceSessionEnded';
      const localized = i18n.t(msgKey);
      const message =
        localized !== msgKey
          ? localized
          : error.response?.data?.message || 'The application is in maintenance mode.';
      return Promise.reject(new Error(message));
    }

    // Handle authentication errors (401 Unauthorized)
    // 403 Forbidden is an authorization issue (user is authenticated but lacks permission),
    // so we should NOT log them out - let the calling code handle it
    if (error.response?.status === 401) {
      // Clear localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Call global logout handler if available (to update AuthContext state)
      if (globalLogoutHandler) {
        globalLogoutHandler();
      }
      
      // Redirect to login page
      // Use window.location.href for a full page reload to ensure clean state
      window.location.href = '/login';
      
      // Prevent further error handling
      const primaryKey = 'apiErrors.AUTH.SESSION_EXPIRED';
      const fallbackKey = 'auth.sessionExpired';
      const primary = i18n.t(primaryKey);
      const fallback = i18n.t(fallbackKey);
      const message = primary !== primaryKey
        ? primary
        : (fallback !== fallbackKey ? fallback : 'Session expired. Please login again.');
      return Promise.reject(new Error(message));
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
  changePassword: (passwords) => api.put('/auth/change-password', passwords),
  logout: () => api.post('/auth/logout'),
  getMaintenanceStatus: () => api.get('/auth/maintenance-status').then((res) => res.data),
};

// Users API
export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  resetPassword: (id, data) => api.put(`/users/${id}/reset-password`, data),
  getByRole: (role) => api.get(`/users/role/${role}`),
  getAccessControlOptions: () => api.get('/users/access-control/options'),
  getOperators: () => api.get('/users/operators/list'),
  registerOperator: (data) => api.post('/users/register-operator', data).then(res => res.data),
};

// Clients API
export const clientsAPI = {
  getAll: (params) => api.get('/clients', { params }),
  getById: (id) => api.get(`/clients/${id}`),
  getBillingStatus: (id) => api.get(`/clients/${id}/billing-status`).then((res) => res.data),
  create: (data) => api.post('/clients', data),
  peseurQuickCreate: (data) => api.post('/clients/peseur-quick', data),
  peseurPonctuelCreate: (data) => api.post('/clients/peseur-ponctuel', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  getStats: (id, params) => api.get(`/clients/${id}/stats`, { params }),
  createLogin: (id, data) => api.post(`/clients/${id}/create-login`, data),
  export: () => api.get('/clients/export', { responseType: 'blob' }),
  import: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/clients/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Transporteurs API
export const transporteursAPI = {
  getAll: (params) => api.get('/transporteurs', { params }),
  getById: (id) => api.get(`/transporteurs/${id}`),
  create: (data) => api.post('/transporteurs', data),
  update: (id, data) => api.put(`/transporteurs/${id}`, data),
  delete: (id) => api.delete(`/transporteurs/${id}`),
  createLogin: (id, data) => api.post(`/transporteurs/${id}/create-login`, data),
};

// Drivers API (Driver entity - person, optional login)
export const driversAPI = {
  getAll: (params) => api.get('/drivers', { params }),
  getById: (id) => api.get(`/drivers/${id}`),
  /** Query: firstName, lastName, phone — returns { match, driver? } */
  match: (params) => api.get('/drivers/match', { params }),
  /** Paginated loads for a driver (manager/peseur/admin). Query: page, limit, clientId, startDate, endDate */
  getLoads: (driverId, params) => api.get(`/drivers/${driverId}/loads`, { params }),
  create: (data) => api.post('/drivers', data),
  update: (id, data) => api.put(`/drivers/${id}`, data),
  delete: (id) => api.delete(`/drivers/${id}`),
  createLogin: (id, data) => api.post(`/drivers/${id}/create-login`, data),
};

// Trucks API
export const trucksAPI = {
  getAll: (params) => api.get('/trucks', { params }),
  getById: (id) => api.get(`/trucks/${id}`),
  /** Paginated loads for a truck (manager/peseur/admin). Query: page, limit, clientId, startDate, endDate */
  getLoads: (truckId, params) => api.get(`/trucks/${truckId}/loads`, { params }),
  create: (data) => api.post('/trucks', data),
  update: (id, data) => api.put(`/trucks/${id}`, data),
  delete: (id) => api.delete(`/trucks/${id}`),
  getStats: (id, params) => api.get(`/trucks/${id}/stats`, { params }),
  getByClient: (clientId) => api.get(`/trucks/client/${clientId}`),
};

// Products API
export const productsAPI = {
  getAll: (params) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  peseurQuickCreate: (data) => api.post('/products/peseur-quick', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  getLoads: (id, params) => api.get(`/products/${id}/loads`, { params }),
  export: () => api.get('/products/export', { responseType: 'blob' }),
  import: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/products/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Ports API
export const portsAPI = {
  getAll: () => api.get('/ports'),
  getById: (id) => api.get(`/ports/${id}`),
  create: (data) => api.post('/ports', data),
  update: (id, data) => api.put(`/ports/${id}`, data),
  delete: (id) => api.delete(`/ports/${id}`),
};

// Moles API
export const molesAPI = {
  getAll: (params) => api.get('/moles', { params }),
  getById: (id) => api.get(`/moles/${id}`),
  create: (data) => api.post('/moles', data),
  update: (id, data) => api.put(`/moles/${id}`, data),
  delete: (id) => api.delete(`/moles/${id}`),
  getByPort: (portId, params) => api.get(`/moles/port/${portId}`, { params }),
};

// Weighbridges API
export const weighbridgesAPI = {
  getAll: (params) => api.get('/weighbridges', { params }),
  getById: (id) => api.get(`/weighbridges/${id}`),
  create: (data) => api.post('/weighbridges', data),
  update: (id, data) => api.put(`/weighbridges/${id}`, data),
  delete: (id) => api.delete(`/weighbridges/${id}`),
  getByPort: (portId) => api.get(`/weighbridges/port/${portId}`),
};

// Loads API
export const loadsAPI = {
  getAll: (params = {}) => {
    const p = { ...params };
    if (Array.isArray(p.status)) {
      p.status = p.status.filter(Boolean).join(',');
    }
    return api.get('/loads', { params: p });
  },
  getById: (id) => api.get(`/loads/${id}`),
  /** Peseur: tare first without DDP/client; bind on return via bindAndWeigh */
  /** @param {string} plate - raw or partial matricule (normalized server-side) @param {Record<string, string>} [params] e.g. { partial: 'true' } for type-ahead substring match */
  openTareByPlate: (plate, params = {}) =>
    api.get('/loads/open-tare-by-plate', { params: { plate, ...params } }),
  requestedByPlate: (plate, params = {}) =>
    api.get('/loads/requested-by-plate', { params: { plate, ...params } }),
  peseurDeferredTare: (data) => api.post('/loads/peseur-deferred-tare', data),
  bindAndWeigh: (id, data) => api.post(`/loads/${id}/bind-and-weigh`, data),
  create: (data) => api.post('/loads', data),
  update: (id, data) => api.put(`/loads/${id}`, data),
  updateStatus: (id, statusOrPayload) => {
    const payload = typeof statusOrPayload === 'string'
      ? { status: statusOrPayload }
      : statusOrPayload;
    return api.put(`/loads/${id}/status`, payload);
  },
  delete: (id) => api.delete(`/loads/${id}`),
  sendTicket: (id, data) => api.post(`/loads/${id}/send-ticket`, data),
  getTicketPDF: (id, lang = 'fr') => api.get(`/loads/${id}/ticket/pdf?lang=${lang}`, { responseType: 'blob' }),
  downloadTicket: (id, lang = 'fr') => api.get(`/loads/${id}/ticket/pdf?lang=${lang}`, { responseType: 'blob' }),
};

// Tickets API
export const ticketsAPI = {
  getAll: (params) => api.get('/tickets', { params }),
  getById: (id) => api.get(`/tickets/${id}`),
  getPDF: (id, lang = 'fr') => api.get(`/tickets/${id}/pdf`, { responseType: 'blob', params: { lang } }),
  sendEmail: (id, data) => api.post(`/tickets/${id}/email`, data),
};

// Certificates API
export const certificatesAPI = {
  getAll: (params) => api.get('/certificates', { params }),
  getById: (id) => api.get(`/certificates/${id}`),
  generate: (data) => api.post('/certificates/generate', data),
  generateDaily: (data) => api.post('/certificates/generate-daily', data),
  review: (id, data) => api.put(`/certificates/${id}/review`, data),
  getAvailableClients: (params) => api.get('/certificates/available-clients', { params }),
  getAvailableDates: (clientId) => api.get(`/certificates/available-dates/${clientId}`),
  previewDaily: (clientId, date) => api.get(`/certificates/preview-daily/${clientId}/${date}`),
  getPDF: (id, params = {}) =>
    api.get(`/certificates/${id}/pdf`, { responseType: 'blob', params: { ...params, lang: params.lang ?? 'fr' } }),
  sendEmail: (id, data) => api.post(`/certificates/${id}/email`, data),
  delete: (id) => api.delete(`/certificates/${id}`),
};

// Bills API
export const billsAPI = {
  getAll: (params) => api.get('/bills', { params }),
  getFilterClients: (params) => api.get('/bills/filter-clients', { params }),
  getById: (id) => api.get(`/bills/${id}`),
  generate: (data) => api.post('/bills/generate', data),
  update: (id, data) => api.put(`/bills/${id}`, data),
  markPaidVerified: (id, data) => api.post(`/bills/${id}/mark-paid-verified`, data || {}),
  getPDF: (id, lang = 'fr') => api.get(`/bills/${id}/pdf`, { responseType: 'blob', params: { lang } }),
  sendEmail: (id, data) => api.post(`/bills/${id}/email`, data),
  delete: (id) => api.delete(`/bills/${id}`),
  getStats: (params = {}) => api.get('/bills/stats', { params }),
  getRecent: (params = {}) => api.get('/bills/recent', { params }),
};

// Payments API
export const paymentsAPI = {
  getAll: (params) => api.get('/payments', { params }),
  getById: (id) => api.get(`/payments/${id}`),
  create: (data) => api.post('/payments', data),
  update: (id, data) => api.put(`/payments/${id}`, data),
  approve: (id) => api.put(`/payments/${id}/approve`),
  reject: (id, data) => api.put(`/payments/${id}/reject`, data),
  getByBill: (billId) => api.get(`/payments/bill/${billId}`),
  processOnline: (data) => api.post('/payments/process-online', data),
  getStats: () => api.get('/payments/stats'),
  getRecent: (params = {}) => api.get('/payments/recent', { params }),
  getReceiptPdf: (id) => api.get(`/payments/${id}/receipt`, { responseType: 'blob' }),
};

// Reports API
export const reportsAPI = {
  generateDaily: (data) => api.post('/reports/daily', data),
  getDailyPDF: (date, lang = 'fr') =>
    api.get(`/reports/daily/${date}/pdf`, { params: { lang }, responseType: 'blob' }),
  sendDailyEmail: (date, data) =>
    api.post(`/reports/daily/${date}/email`, { ...data, lang: data?.lang ?? 'fr' }),
  getDashboard: () => api.get('/reports/dashboard'),
  getDailyReports: (params) => api.get('/reports/daily', { params }),
  getManagers: () => api.get('/reports/managers'),
  getReportPeseurs: () => api.get('/reports/peseurs'),
  getDailyTotalsByWeighbridgeProduct: (params) => api.get('/reports/daily-totals-by-weighbridge-product', { params }),
  getReportDayLoads: (params) => api.get('/reports/daily-totals/loads', { params }),
  getLoadDates: (params) => api.get('/reports/load-dates', { params }),
  getRapportJournalierWeighbridges: () => api.get('/reports/rapport-journalier/weighbridges'),
  getRapportJournalierPeseurs: (weighbridgeId) =>
    api.get('/reports/rapport-journalier/peseurs', { params: { weighbridgeId } }),
  getRapportJournalierPDF: (params) =>
    api.get('/reports/rapport-journalier/pdf', { params, responseType: 'blob' }),
};

// GPS Tracking API
export const gpsAPI = {
  getLocations: (params) => api.get('/gps/locations', { params }),
  getLatestLocations: (params) => api.get('/gps/locations/latest', { params }),
  getReturningTrucks: (params) => api.get('/gps/returning-trucks', { params }),
  updateLocation: (data) => api.post('/gps/locations', data),
  getTruckHistory: (truckId, params) => api.get(`/gps/trucks/${truckId}/history`, { params }),
  getTrips: (params) => api.get('/gps/trips', { params }),
  createTrip: (data) => api.post('/gps/trips', data),
  updateTripStatus: (tripId, data) => api.put(`/gps/trips/${tripId}/status`, data),
};

// Financial Reports API (for SUPER users)
export const financialReportsAPI = {
  getOverview: (params) => api.get('/financial-reports/overview', { params }),
  getRevenueTrends: (params) => api.get('/financial-reports/revenue-trends', { params }),
  getClientPerformance: (params) => api.get('/financial-reports/client-performance', { params }),
  getPaymentMethods: (params) => api.get('/financial-reports/payment-methods', { params }),
  getInfrastructureBreakdown: (params) => api.get('/financial-reports/infrastructure-breakdown', { params })
};

// Admin statistics (ADMIN only)
export const adminStatsAPI = {
  getUploadStorage: () => api.get('/admin-stats/upload-storage'),
};

// Error Logs API (for ADMIN users)
export const errorLogsAPI = {
  getAll: (params) => api.get('/error-logs', { params }),
  getStats: () => api.get('/error-logs/stats'),
  getTypes: () => api.get('/error-logs/types'),
  getClients: () => api.get('/error-logs/clients'),
  delete: (id) => api.delete(`/error-logs/${id}`),
  deleteBatch: (ids) => api.delete('/error-logs/batch', { data: { ids } }),
  clearLogs: () => api.delete('/error-logs'),
  logError: (data) => api.post('/error-logs/log', data),
  getConfig: () => api.get('/error-logs/config'),
  updateConfig: (data) => api.put('/error-logs/config', data),
};

// Client Credits API
export const clientCreditsAPI = {
  getBalance: (clientId) => api.get(`/client-credits/${clientId}/balance`),
  getTransactions: (clientId, params) => api.get(`/client-credits/${clientId}/transactions`, { params }),
  getPendingCreditTopupBills: (clientId) =>
    api.get(`/client-credits/${clientId}/pending-credit-topup-bills`),
  requestCreditTopupBill: (clientId, data) =>
    api.post(`/client-credits/${clientId}/credit-topup-bill`, data),
  adjust: (clientId, data) => api.post(`/client-credits/${clientId}/adjust`, data),
};

// System Config API (for ADMIN users)
export const systemConfigAPI = {
  getAll: () => api.get('/system-config'),
  getByKey: (key) => api.get(`/system-config/${key}`),
  createOrUpdate: (data) => api.post('/system-config', data),
  update: (key, data) => api.put(`/system-config/${key}`, data),
  delete: (key) => api.delete(`/system-config/${key}`),
  getSenegalHolidays: () => api.get('/system-config/holidays/senegal'),
  updateSenegalHolidays: (holidays) => api.put('/system-config/holidays/senegal', { holidays }),
  initializeSenegalHolidays: (years = []) => api.post('/system-config/holidays/senegal/initialize', { years }),
  // Tariff config (TARIFS POIDS PUBLICS)
  getTariffs: () => api.get('/system-config/tariffs'),
  initializeTariffs: () => api.post('/system-config/tariffs/initialize'),
  updateTarifPoids: (data) => api.put('/system-config/tariffs/taxes-au-poids', data),
  updateTsOptions: (tsOptions) => api.put('/system-config/tariffs/taxes-supplementaires', { tsOptions }),
  updateTvaRate: (tvaRate) => api.put('/system-config/tariffs/tva-rate', { tvaRate }),
  getMailingStatus: () => api.get('/system-config/mailing/status'),
  getMailing: () => api.get('/system-config/mailing'),
  updateMailing: (data) => api.put('/system-config/mailing', data),
  testMailing: () => api.post('/system-config/mailing/test'),
  getTicketLetterhead: () => api.get('/system-config/ticket-letterhead').then((res) => res.data),
  updateTicketLetterhead: (payload) =>
    api.put('/system-config/ticket-letterhead', payload).then((res) => res.data),
  uploadTicketLetterheadLogo: (formData) =>
    api.post('/system-config/ticket-letterhead/logo', formData, {
      transformRequest: [(data, headers) => {
        if (data instanceof FormData) {
          delete headers['Content-Type'];
        }
        return data;
      }]
    }).then((res) => res.data),
  deleteTicketLetterheadLogo: () =>
    api.delete('/system-config/ticket-letterhead/logo').then((res) => res.data),
  getBillingPdfOptions: () => api.get('/system-config/billing/pdf-options').then((res) => res.data),
  updateBillingPdfOptions: (payload) =>
    api.put('/system-config/billing/pdf-options', payload).then((res) => res.data),
  getLocale: () => api.get('/system-config/locale'),
  updateLocale: (payload) => api.put('/system-config/locale', payload),
};

// DDP API
export const ddpsAPI = {
  create: (data) => api.post('/ddps', data).then(res => res.data),
  getAll: () => api.get('/ddps').then(res => res.data),
  getMy: () => api.get('/ddps/my').then(res => {
    const raw = res?.data?.ddps ?? res?.data;
    return Array.isArray(raw) ? raw : [];
  }),
  getById: (id) => api.get(`/ddps/${id}`).then(res => res.data),
  updateStatus: (id, data) => api.patch(`/ddps/${id}`, data).then(res => res.data),
  update: (id, data) => api.put(`/ddps/${id}`, data).then(res => res.data),
  delete: (id) => api.delete(`/ddps/${id}`).then(res => res.data),
  listForOperator: () => ddpsAPI.getMy(),
};

export const notificationsAPI = {
  getMine: () => api.get('/notifications/mine').then((res) => res.data),
  getManager: () => api.get('/notifications/manager').then((res) => res.data),
  markRead: (id) => api.post(`/notifications/${id}/read`).then((res) => res.data),
  dismiss: (id) => api.post(`/notifications/${id}/dismiss`).then((res) => res.data),
  dismissLive: (entityType, entityId) => api.post('/notifications/live-dismiss', { entityType, entityId }).then((res) => res.data),
};

// OOP API
export const oopsAPI = {
  create: (data) => api.post('/oops', data).then(res => res.data),
  getMy: () => api.get('/oops/my').then(res => res.data),
};

export default api;